import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Trophy,
  Plus,
  Calendar,
  MapPin,
  Users,
  ArrowRight,
  Trash2,
  Search,
  Sparkles,
  ShieldCheck,
  Radio,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { API } from '../lib/api';
import { cn } from '../lib/utils';
import { ContextualState, PageLoadingState } from '../components/StatePanel';

const statusFilters = ['all', 'live', 'scheduled', 'completed'];

const Tournaments = () => {
  const { getAuthHeader, isOrganizer } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    format: 'league',
    overs_per_match: 20,
    start_date: '',
    venue: '',
    entry_fee: 0,
  });

  const fetchTournaments = useCallback(async (showError = true) => {
    try {
      const response = isOrganizer()
        ? await axios.get(`${API}/tournaments?organizer_only=true`, getAuthHeader())
        : await axios.get(`${API}/tournaments/public`);
      setTournaments(response.data);
    } catch (error) {
      if (showError) toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  }, [isOrganizer, getAuthHeader]);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  useAutoRefresh(() => fetchTournaments(false));

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!formData.name || !formData.start_date) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/tournaments`, formData, getAuthHeader());
      setTournaments([response.data, ...tournaments]);
      setDialogOpen(false);
      setFormData({
        name: '',
        format: 'league',
        overs_per_match: 20,
        start_date: '',
        venue: '',
        entry_fee: 0,
      });
      toast.success('Tournament created!');
      navigate(`/tournaments/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create tournament');
    }
  };

  const handleDelete = async (id, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this tournament?')) return;

    try {
      await axios.delete(`${API}/tournaments/${id}`, getAuthHeader());
      setTournaments(tournaments.filter((tournament) => tournament.id !== id));
      toast.success('Tournament deleted');
    } catch (error) {
      toast.error('Failed to delete tournament');
    }
  };

  const filteredTournaments = useMemo(() => {
    return tournaments.filter((tournament) => {
      const matchesSearch =
        tournament.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tournament.venue?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' ? true : tournament.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [tournaments, searchQuery, statusFilter]);

  const getStatusTone = (status) => {
    switch (status) {
      case 'live':
        return 'bg-red-500 text-white';
      case 'completed':
        return 'bg-emerald-600 text-white';
      case 'scheduled':
      case 'upcoming':
        return 'bg-sky-600 text-white';
      default:
        return 'bg-white/10 text-white/70';
    }
  };

  const summary = {
    total: tournaments.length,
    live: tournaments.filter((item) => item.status === 'live').length,
    completed: tournaments.filter((item) => item.status === 'completed').length,
    scheduled: tournaments.filter((item) => item.status === 'scheduled').length,
  };

  return (
    <Layout title="Tournaments">
      <div className="space-y-6">
        <Card className="overflow-hidden rounded-[24px] border-primary/20 bg-[linear-gradient(135deg,rgba(170,255,0,0.14),rgba(255,255,255,0.03)_55%,rgba(255,255,255,0.01))] sm:rounded-[30px]">
          <CardContent className="p-4 sm:p-8">
            <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  {isOrganizer() ? (
                    <Badge className="rounded-full border border-primary/20 bg-primary/10 text-primary">
                      <ShieldCheck className="mr-1 h-3 w-3" />
                      Organizer Workspace
                    </Badge>
                  ) : (
                    <Badge className="rounded-full border border-white/10 bg-white/[0.05] text-white/70">
                      <Sparkles className="mr-1 h-3 w-3 text-primary" />
                      Tournament Hub
                    </Badge>
                  )}
                </div>

                <h1 className="mt-3 text-[31px] font-bold leading-[1.08] tracking-tight text-white sm:mt-4 sm:text-4xl">
                  {isOrganizer() ? 'Manage every tournament from one place' : 'Browse live and upcoming tournaments'}
                </h1>
                <p className="mt-2.5 max-w-2xl text-[13px] leading-5 text-white/65 sm:mt-3 sm:text-sm sm:leading-6">
                  {isOrganizer()
                    ? 'Create new competitions, monitor live status, and jump directly into team, schedule, standings, and announcement workflows.'
                    : 'Discover active competitions, explore tournament hubs, and move quickly into live matches, standings, and updates.'}
                </p>

                {isOrganizer() && (
                  <div className="mt-4 sm:mt-6">
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="rounded-full px-5" data-testid="create-tournament-button">
                          <Plus className="mr-2 h-4 w-4" />
                          New Tournament
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="border-white/10 bg-[#0b1017] text-white sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>Create Tournament</DialogTitle>
                          <DialogDescription className="text-white/55">
                            Set up a new cricket tournament workspace.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreate} className="mt-4 space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">Tournament Name *</Label>
                            <Input
                              id="name"
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              placeholder="Premier Cricket League 2026"
                              className="border-white/10 bg-black/20 text-white"
                              data-testid="tournament-name-input"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Format</Label>
                              <Select value={formData.format} onValueChange={(value) => setFormData({ ...formData, format: value })}>
                                <SelectTrigger className="border-white/10 bg-black/20 text-white" data-testid="tournament-format-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="league">League</SelectItem>
                                  <SelectItem value="knockout">Knockout</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="overs">Overs per Match</Label>
                              <Input
                                id="overs"
                                type="number"
                                value={formData.overs_per_match}
                                onChange={(e) => setFormData({ ...formData, overs_per_match: parseInt(e.target.value, 10) })}
                                min={1}
                                max={50}
                                className="border-white/10 bg-black/20 text-white"
                                data-testid="tournament-overs-input"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="start_date">Start Date *</Label>
                            <Input
                              id="start_date"
                              type="date"
                              value={formData.start_date}
                              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                              className="border-white/10 bg-black/20 text-white"
                              data-testid="tournament-date-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="venue">Venue</Label>
                            <Input
                              id="venue"
                              value={formData.venue}
                              onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                              placeholder="City Cricket Stadium"
                              className="border-white/10 bg-black/20 text-white"
                              data-testid="tournament-venue-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="entry_fee">Entry Fee (₹)</Label>
                            <Input
                              id="entry_fee"
                              type="number"
                              value={formData.entry_fee}
                              onChange={(e) => setFormData({ ...formData, entry_fee: parseFloat(e.target.value || 0) })}
                              min={0}
                              className="border-white/10 bg-black/20 text-white"
                              data-testid="tournament-fee-input"
                            />
                          </div>

                          <div className="flex gap-3 pt-4">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-white/10 bg-black/20 text-white hover:bg-white/[0.06]">
                              Cancel
                            </Button>
                            <Button type="submit" className="flex-1" data-testid="tournament-submit-button">
                              Create Tournament
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2.5 sm:gap-3 xl:grid-cols-2">
                <Card className="rounded-[22px] border-white/10 bg-black/20 sm:rounded-3xl">
                  <CardContent className="p-3.5 text-center sm:p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 sm:text-[11px] sm:tracking-[0.24em]">Total</p>
                    <p className="mt-1.5 text-[32px] font-bold text-white score-display sm:mt-2 sm:text-3xl">{summary.total}</p>
                  </CardContent>
                </Card>
                <Card className="rounded-[22px] border-white/10 bg-black/20 sm:rounded-3xl">
                  <CardContent className="p-3.5 text-center sm:p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 sm:text-[11px] sm:tracking-[0.24em]">Live</p>
                    <p className="mt-1.5 text-[32px] font-bold text-red-300 score-display sm:mt-2 sm:text-3xl">{summary.live}</p>
                  </CardContent>
                </Card>
                <Card className="rounded-[22px] border-white/10 bg-black/20 sm:rounded-3xl">
                  <CardContent className="p-3.5 text-center sm:p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 sm:text-[11px] sm:tracking-[0.24em]">Scheduled</p>
                    <p className="mt-1.5 text-[32px] font-bold text-white score-display sm:mt-2 sm:text-3xl">{summary.scheduled}</p>
                  </CardContent>
                </Card>
                <Card className="rounded-[22px] border-white/10 bg-black/20 sm:rounded-3xl">
                  <CardContent className="p-3.5 text-center sm:p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 sm:text-[11px] sm:tracking-[0.24em]">Completed</p>
                    <p className="mt-1.5 text-[32px] font-bold text-white score-display sm:mt-2 sm:text-3xl">{summary.completed}</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-white/10 bg-white/[0.04] sm:rounded-[28px]">
          <CardContent className="p-3.5 sm:p-5">
            <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by tournament name or venue"
                  className="h-11 rounded-2xl border-white/10 bg-black/20 pl-10 text-white sm:h-12"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {statusFilters.map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setStatusFilter(filter)}
                      className={cn(
                        'rounded-full border px-2.5 py-1.5 text-[13px] font-medium capitalize transition-all sm:px-3 sm:py-2 sm:text-sm',
                        statusFilter === filter
                          ? 'border-primary/30 bg-primary/12 text-primary'
                          : 'border-white/10 bg-white/[0.04] text-white/58 hover:bg-white/[0.08] hover:text-white'
                      )}
                    >
                      <span className="inline-flex items-center gap-2">
                        <Filter className="h-3.5 w-3.5" />
                        {filter}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[13px] text-white/50 sm:text-sm">
                Showing <span className="font-semibold text-white">{filteredTournaments.length}</span> tournament{filteredTournaments.length === 1 ? '' : 's'}
              </p>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <PageLoadingState summaryCards={3} body="list" className="pt-2" />
        ) : filteredTournaments.length === 0 ? (
          <ContextualState
            icon={Trophy}
            eyebrow="Tournament Directory"
            title={isOrganizer() ? 'No tournaments match your workspace filters' : 'No tournaments available'}
            description={
              isOrganizer()
                ? 'Try changing the filters or create a new tournament.'
                : 'Check back soon for live competitions and upcoming fixtures.'
            }
            tone="dark"
            action={
              isOrganizer() ? (
                <Button onClick={() => setDialogOpen(true)} className="rounded-full">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Tournament
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 sm:gap-4" data-testid="tournaments-grid">
            {filteredTournaments.map((tournament, index) => (
              <Link
                key={tournament.id}
                to={`/tournaments/${tournament.id}`}
                className="stagger-in"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`tournament-card-${tournament.id}`}
              >
                <Card className="h-full rounded-[22px] border-white/10 bg-white/[0.04] transition-all hover:-translate-y-0.5 hover:bg-white/[0.07] sm:rounded-[28px]">
                  <CardHeader className="pb-2.5 sm:pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 sm:h-12 sm:w-12">
                          <Trophy className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
                        </div>
                        <div className="min-w-0">
                          <CardTitle className="truncate text-base text-white sm:text-lg">{tournament.name}</CardTitle>
                          <Badge variant="outline" className="mt-1.5 rounded-full border-white/10 bg-white/[0.04] text-[11px] capitalize text-white/70 sm:mt-2">
                            {tournament.format}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Badge className={cn('rounded-full capitalize', getStatusTone(tournament.status))}>
                          {tournament.status}
                        </Badge>
                        {isOrganizer() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full text-white/50 hover:bg-white/[0.08] hover:text-red-300 sm:h-9 sm:w-9"
                            onClick={(event) => handleDelete(tournament.id, event)}
                            data-testid={`delete-tournament-${tournament.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3.5 sm:space-y-4">
                    <div className="space-y-1.5 text-[13px] text-white/58 sm:space-y-2 sm:text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        <span>{new Date(tournament.start_date).toLocaleDateString()}</span>
                      </div>
                      {tournament.venue && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-primary" />
                          <span className="truncate">{tournament.venue}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        {tournament.status === 'live' ? (
                          <Radio className="h-4 w-4 text-red-300" />
                        ) : (
                          <Users className="h-4 w-4 text-primary" />
                        )}
                        <span>{tournament.overs_per_match} overs per match</span>
                      </div>
                    </div>

                    {tournament.entry_fee > 0 && (
                      <div className="rounded-2xl border border-white/10 bg-black/20 px-3.5 py-2.5 sm:px-4 sm:py-3">
                        <span className="text-[13px] font-medium text-white sm:text-sm">Entry Fee: ₹{tournament.entry_fee}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-end text-primary">
                      <span className="text-[13px] font-medium sm:text-sm">Open Tournament Hub</span>
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Tournaments;
